import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { getEffectiveAuthenticatedAccountId } from "@/lib/accountContext";
import { apiFetch, buildParams } from "@/lib/api";
import { myRolesQueryKey } from "@/lib/authQueryCache";
import type { Role } from "@/lib/permissions";
import { SELECTABLE_ROLES } from "@/lib/permissions";
import { useUserStore } from "@/store/userStore";

export function getSelectableRoles(roleCodes: string[]): Role[] {
  return roleCodes
    .filter((code): code is Role => (SELECTABLE_ROLES as string[]).includes(code))
    .sort((a, b) => SELECTABLE_ROLES.indexOf(a) - SELECTABLE_ROLES.indexOf(b));
}

export function useMyRoles() {
  const authenticatedAccountId = useUserStore((s) => s.authenticatedAccountId);
  const initializeRoles = useUserStore((s) => s.initializeRoles);
  const hasHydrated = useUserStore((s) => s.hasHydrated);
  const resolvedAccountId = getEffectiveAuthenticatedAccountId(authenticatedAccountId) ?? "";
  const query = buildParams({
    account_id: resolvedAccountId,
  });

  const result = useQuery({
    queryKey: [...myRolesQueryKey, resolvedAccountId] as const,
    queryFn: () => apiFetch<string[]>(`/v1/project/my-roles${query}`),
    enabled: !!resolvedAccountId && hasHydrated,
  });

  useEffect(() => {
    if (result.data) {
      initializeRoles(getSelectableRoles(result.data));
    }
  }, [result.data, initializeRoles]);

  return result;
}
